import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Logo } from "@/components/brand/Logo";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from "@/integrations/supabase/client";
import { isLevel, LICENSE_NUMBER } from "@/config/compliance";

const LICENSE_DISCLOSURE =
  "A Family Wealth Strategist is a person licensed by their resident state to sell fixed life, accident, and health insurance products. No variable or market-based products are offered or indicated." +
  (LICENSE_NUMBER ? ` Georgia Life, Health & Annuity Producer License #${LICENSE_NUMBER}.` : "");

const COMPLIANCE_DISCLAIMER =
  "Currency Thoughts LLC is an independent insurance agency. Products and availability vary by state. All policy benefits are governed by the actual policy contract issued by the insurance carrier. Nothing on this website constitutes legal, tax, or investment advice. Currency Thoughts LLC provides financial education. We do not provide legal, tax, or investment advisory services. Educational content on this site is general in nature and is not a recommendation to purchase any specific product.";

const COLUMNS = [
  {
    heading: "Solutions",
    links: [
      { to: "/solutions", hash: "family-protection", label: "Family Protection" },
      { to: "/solutions", hash: "generational-wealth", label: "Generational Wealth" },
      { to: "/solutions", hash: "retirement-strategy", label: "Retirement Strategy" },
      { to: "/solutions", hash: "business-continuity", label: "Business Continuity" },
    ],
  },
  {
    heading: "Company",
    links: [
      { to: "/about", hash: undefined, label: "About" },
      { to: "/contact", hash: undefined, label: "Contact" },
      { to: "/careers", hash: undefined, label: "Careers — Become a Strategist" },
      ...(isLevel("full")
        ? [{ to: "/book" as const, hash: undefined, label: "Book Consultation" }]
        : []),
    ],
  },
  {
    heading: "Resources",
    links: [
      { to: "/academy", hash: undefined, label: "Academy" },
      { to: "/family-capital-system", hash: undefined, label: "The Family Capital System" },
      { to: "/academy/library", hash: undefined, label: "Family Library" },
      { to: "/login", hash: undefined, label: "Client Login" },
      { to: "/contact", hash: undefined, label: "Support" },
    ],
  },
];

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { isAdmin } = useUserRoles();
  const [licensedStates, setLicensedStates] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("state_licenses")
        .select("state_code, sort_order")
        .eq("active", true)
        .order("sort_order");
      if (!cancelled && data) {
        const codes = Array.from(new Set(data.map((r) => r.state_code).filter(Boolean)));
        setLicensedStates(codes);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer className="bg-navy-900 text-cream-50">
      <FiligreeFrame intensity="subtle">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-10 md:py-20">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
            {/* Brand column */}
            <div className="lg:col-span-1">
              <Logo variant="light" showTagline />
              <p className="mt-6 max-w-xs font-sans text-[15px] leading-relaxed text-cream-50/75">
                Currency Thoughts is a Family Wealth Strategy Firm. Our
                strategists serve as Family Wealth Strategists and Economic
                Empowerment Coaches — veteran-led, family-focused, education-first.
              </p>
            </div>

            {COLUMNS.map((col) => (
              <div key={col.heading}>
                <Eyebrow color="cream" align="left">
                  {col.heading}
                </Eyebrow>
                <ul className="mt-5 space-y-3">
                  {col.links.map((link) => (
                    <li key={`${col.heading}-${link.label}`}>
                      <Link
                        to={link.to}
                        hash={link.hash}
                        className="font-sans text-sm text-cream-50/80 transition-colors hover:text-bronze-400"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bronze hairline divider */}
          <div className="my-10 h-px w-full bg-bronze-600/50" aria-hidden />

          <div className="flex flex-col items-center gap-6 text-center">
            {licensedStates.length > 0 && (
              <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-cream-50/75">
                <span className="text-bronze-400">Licensed in:</span>{" "}
                {licensedStates.join(", ")}
              </p>
            )}
            <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-sans text-xs uppercase tracking-[0.18em] text-cream-50/80">
              <Link to="/privacy" className="hover:text-bronze-400">Privacy</Link>
              <span className="text-bronze-600/50" aria-hidden>•</span>
              <Link to="/terms" className="hover:text-bronze-400">Terms</Link>
              <span className="text-bronze-600/50" aria-hidden>•</span>
              <Link to="/accessibility" className="hover:text-bronze-400">Accessibility</Link>
              <span className="text-bronze-600/50" aria-hidden>•</span>
              <Link to="/disclosures" className="hover:text-bronze-400">Disclosures</Link>
              <span className="text-bronze-600/50" aria-hidden>•</span>
              <Link to="/settings/privacy" className="hover:text-bronze-400">Cookie Settings</Link>
            </nav>
            <p className="font-sans text-xs uppercase tracking-[0.18em] text-cream-50/70">
              © Currency Thoughts LLC · 2021–{currentYear} ·{" "}
              All rights reserved
              {isAdmin && (
                <>
                  {" "}·{" "}
                  <Link to="/admin" className="text-bronze-400 hover:text-cream-50">
                    Admin
                  </Link>
                </>
              )}
            </p>
            <p
              className="mx-auto font-sans text-[11px] leading-relaxed text-cream-50/60"
              style={{ maxWidth: "700px" }}
            >
              {LICENSE_DISCLOSURE}
            </p>
            <p
              className="mx-auto font-sans text-[11px] leading-relaxed text-cream-50/60"
              style={{ maxWidth: "700px" }}
            >
              {COMPLIANCE_DISCLAIMER}
            </p>
          </div>
        </div>
      </FiligreeFrame>
    </footer>
  );
}

export default Footer;